import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import LearnerDashboard from "./learnerDashboard";
import SponsorDashboard from "./sponsorDashboard";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { toast } from "react-hot-toast";

// ── Shared card style for role selection ──────────────────────────────────────
function RoleCard({ selected, emoji, title, description, onClick }) {
    return (
        <div
            onClick={onClick}
            style={{
                cursor: "pointer",
                border: `2px solid ${selected ? "var(--primary-color)" : "var(--border-color)"}`,
                borderRadius: 16,
                padding: "28px 16px",
                flex: 1,
                textAlign: "center",
                background: selected ? "var(--bg-light)" : "var(--bg-white)",
                boxShadow: selected ? "var(--shadow-md)" : "none",
                transition: "all 0.2s",
            }}
        >
            <div style={{ fontSize: 36, marginBottom: 10 }}>{emoji}</div>
            <h5 style={{
                fontWeight: 700,
                color: selected ? "var(--primary-color)" : "var(--text-primary)",
                marginBottom: 6,
                fontSize: "1rem",
            }}>
                {title}
            </h5>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: 0 }}>
                {description}
            </p>
        </div>
    );
}

const DashboardRouter = () => {
    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState("");
    const [roleLoading, setRoleLoading] = useState(false);
    const navigate = useNavigate();

    const fetchRole = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("motivar-token");
            if (!token) {
                navigate("/user-auth");
                return;
            }

            // Fast path: returning users with a cached role skip the API call
            const cachedRole = localStorage.getItem("motivar-user-role");
            if (cachedRole === "learner" || cachedRole === "sponsor") {
                setRole(cachedRole);
                setLoading(false);
                return;
            }

            // No cached role — call API to determine (new users or cleared storage)
            // Uses axiosInstance so an expired access token is silently refreshed
            // (via the refresh-token rotation flow) instead of bouncing to /user-auth.
            const response = await axiosInstance.get(`/dashboard`);

            // Learner controller returns flat JSON; sponsor controller wraps in {data:{}}
            const userDetails =
                response.data.userDetails ?? response.data.data?.userDetails;

            if (userDetails?.role === "learner" || userDetails?.role === "sponsor") {
                localStorage.setItem("motivar-user-role", userDetails.role);
                setRole(userDetails.role);
                setShowRoleModal(false);
            } else {
                // Authenticated but no role assigned yet — prompt for selection
                setShowRoleModal(true);
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || "Failed to load dashboard.";

            if (err.response?.status === 400 && msg === "Invalid user role") {
                setShowRoleModal(true);
                return;
            }

            if (err.response?.status === 401) {
                navigate("/user-auth");
                return;
            }

            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchRole();
    }, [fetchRole]);

    const handleSetRole = async () => {
        if (!selectedRole) {
            toast.error("Please choose an account type.");
            return;
        }
        setRoleLoading(true);
        try {
            await axiosInstance.patch("/user/profile/update", { role: selectedRole });
            localStorage.setItem("motivar-user-role", selectedRole);
            toast.success("Account type set! Welcome to Motivar.");
            // Remove any stale signup state
            localStorage.removeItem("motivar-signup-state");
            setShowRoleModal(false);
            setRole(selectedRole);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to set account type. Please try again.");
        } finally {
            setRoleLoading(false);
        }
    };

    // Non-dismissible role selection modal
    const RoleSelectionModal = (
        <Modal show={showRoleModal} backdrop="static" keyboard={false} centered>
            <Modal.Header style={{
                background: "var(--primary-color)",
                borderBottom: "none",
                borderRadius: "0.75rem 0.75rem 0 0",
                padding: "1.2rem 1.5rem",
            }}>
                <Modal.Title style={{
                    color: "white",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                }}>
                    Choose Your Account Type
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: "1.5rem" }}>
                <p style={{
                    color: "var(--text-secondary)",
                    fontSize: 14,
                    marginBottom: "1.5rem",
                    textAlign: "center",
                }}>
                    To continue, please select the account type that best describes you.
                    This is required to access your dashboard.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-4">
                    <RoleCard
                        selected={selectedRole === "learner"}
                        emoji="🎓"
                        title="Learner"
                        description="I want to get my course funded"
                        onClick={() => setSelectedRole("learner")}
                    />
                    <RoleCard
                        selected={selectedRole === "sponsor"}
                        emoji="🤝"
                        title="Sponsor"
                        description="I want to fund learners"
                        onClick={() => setSelectedRole("sponsor")}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer style={{
                borderTop: "1px solid var(--border-color)",
                padding: "1rem 1.5rem",
                justifyContent: "center",
            }}>
                <Button
                    style={{
                        background: selectedRole ? "var(--primary-color)" : "#b2dfdb",
                        border: "none",
                        borderRadius: 8,
                        fontWeight: 600,
                        padding: "0.7rem 2.5rem",
                        cursor: selectedRole ? "pointer" : "not-allowed",
                        fontSize: "1rem",
                    }}
                    onClick={handleSetRole}
                    disabled={!selectedRole || roleLoading}
                >
                    {roleLoading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Setting up...
                        </>
                    ) : (
                        "Continue to Dashboard"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    if (loading) {
        return (
            <>
                {RoleSelectionModal}
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
                    <Spinner animation="border" style={{ color: "var(--primary-color)", width: "3rem", height: "3rem" }} />
                    <p className="mt-3" style={{ color: "var(--text-secondary)" }}>
                        Loading your dashboard...
                    </p>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                {RoleSelectionModal}
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
                    <div style={{
                        background: "#fff3f3",
                        border: "1px solid #f5c6cb",
                        borderRadius: 12,
                        padding: "2rem",
                        maxWidth: 400,
                        textAlign: "center",
                    }}>
                        <p style={{ color: "#721c24", marginBottom: "1rem" }}>
                            {error}
                        </p>
                        <Button
                            onClick={() => navigate("/user-auth")}
                            style={{ background: "var(--primary-color)", border: "none", borderRadius: 8 }}
                        >
                            Go to Sign In
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    if (showRoleModal) {
        return RoleSelectionModal;
    }

    if (role === "learner") return <LearnerDashboard />;
    if (role === "sponsor") return <SponsorDashboard />;

    return null;
};

export default DashboardRouter;
